import React, { useState, useEffect } from "react";
import { TextField, Button, Box, Typography, Autocomplete, Stack, Paper, Fade } from "@mui/material";
import { supabase } from "../supabaseClient";

export default function CharacterMatchupSelector({ onClose }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    async function fetchSessionAndProfile() {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      if (data.session?.user?.id) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", data.session.user.id)
          .single();
        setProfile(profile);
      }
    }
    fetchSessionAndProfile();
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user?.id) {
        supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .single()
          .then(({ data }) => setProfile(data));
      } else {
        setProfile(null);
      }
    });
    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  // Cache important characters
  const [importantChars, setImportantChars] = useState([]);
  useEffect(() => {
    async function fetchImportantChars() {
      const { data } = await supabase
        .from("characters")
        .select("id, name")
        .eq("important", true)
        .order("name", { ascending: true });
      setImportantChars(data || []);
    }
    fetchImportantChars();
  }, []);

  const [char1, setChar1] = useState(null);
  const [char2, setChar2] = useState(null);
  const [search1, setSearch1] = useState("");
  const [search2, setSearch2] = useState("");
  const [options1, setOptions1] = useState([]);
  const [options2, setOptions2] = useState([]);
  const [loading1, setLoading1] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Queue state
  const [queueEntry, setQueueEntry] = useState(null);
  const [queuePosition, setQueuePosition] = useState(null);

  // Fetch queue only once on mount/profile change
  useEffect(() => {
    async function fetchQueueStatus() {
      if (!profile?.username) return;
      const { data: queue } = await supabase
        .from("versus_live_queue")
        .select("id, created_by, created_at")
        .order("created_at", { ascending: true });

      if (!queue) return;

      // Find user's entry
      const userEntry = queue.find(entry => entry.created_by === profile.username);
      setQueueEntry(userEntry);

      if (userEntry) {
        setQueuePosition(queue.findIndex(entry => entry.id === userEntry.id) + 1);
      }
    }
    fetchQueueStatus();
  }, [profile]);

  // Decrement position when a new matchup happens in versus_live
  useEffect(() => {
    // Listen for new matchups in versus_live table
    const subscription = supabase
      .channel('queue_position')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'versus_live' },
        () => {
          // Only decrement if user has a queue position
          setQueuePosition(pos => (pos && pos > 1 ? pos - 1 : pos));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [queuePosition]);

  // Use cached important characters for dropdown if search length >= 2
  useEffect(() => {
    if (search1.length >= 2) {
      const filtered = importantChars.filter(c =>
        c.name.toLowerCase().includes(search1.toLowerCase())
      );
      setOptions1(filtered);
      if (filtered.length === 0) {
        setLoading1(true);
        supabase
          .from("characters")
          .select("id, name")
          .ilike("name", `%${search1}%`)
          .order("name", { ascending: true })
          .limit(10)
          .then(({ data, error }) => {
            setLoading1(false);
            if (error) setOptions1([]);
            else setOptions1(data);
          });
      }
    } else {
      setOptions1([]);
    }
  }, [search1, importantChars]);

  useEffect(() => {
    if (search2.length >= 2) {
      const filtered = importantChars.filter(c =>
        c.name.toLowerCase().includes(search2.toLowerCase())
      );
      setOptions2(filtered);
      if (filtered.length === 0) {
        setLoading2(true);
        supabase
          .from("characters")
          .select("id, name")
          .ilike("name", `%${search2}%`)
          .order("name", { ascending: true })
          .limit(10)
          .then(({ data, error }) => {
            setLoading2(false);
            if (error) setOptions2([]);
            else setOptions2(data);
          });
      }
    } else {
      setOptions2([]);
    }
  }, [search2, importantChars]);

  const filteredOptions2 = options2.filter(opt => opt.id !== (char1 && char1.id));
  const filteredOptions1 = options1.filter(opt => opt.id !== (char2 && char2.id));

  const handleSubmit = async () => {
    setError("");
    setSuccess("");
    if (queueEntry && queuePosition && queuePosition > 0) {
      setError("You already have a matchup in the queue. Please wait until it happens.");
      return;
    }
    if (!char1 || !char2 || char1.id === char2.id) {
      setError("Please select two different characters.");
      return;
    }
    if (!profile?.username) {
      setError("Profile not loaded. Try again.");
      return;
    }
    const { error: insertError } = await supabase
      .from("versus_live_queue")
      .insert([{
        character1_id: char1.id,
        character2_id: char2.id,
        created_by: profile.username,
        created_at: new Date().toISOString(),
      }]);
    if (insertError) {
      setError("Failed to submit matchup. Try again.");
    } else {
      setSuccess("Matchup submitted!");
      setChar1(null);
      setChar2(null);
      setSearch1("");
      setSearch2("");
      setOptions1([]);
      setOptions2([]);
      // Refresh queue status
      setTimeout(async () => {
        const { data: queue } = await supabase
          .from("versus_live_queue")
          .select("id, created_by, created_at")
          .order("created_at", { ascending: true });
        const userEntry = queue.find(entry => entry.created_by === profile.username);
        setQueueEntry(userEntry);
        if (userEntry) {
          setQueuePosition(queue.findIndex(entry => entry.id === userEntry.id) + 1);
        }
      }, 500);
    }
  };

  useEffect(() => {
    window.queuePosition = queuePosition;
  }, [queuePosition]);

  return (
    <Fade in={true}>
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          zIndex: 1400,
          transform: "translate(-50%, -50%)",
          width: { xs: "95vw", sm: 400 },
          maxWidth: 420,
          maxHeight: "90vh",
          borderRadius: 4,
          background: "rgba(35,35,35,0.25)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          color: "#fff",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          overflow: "hidden",
          border: "2px solid #3e3e3e",
          p: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
            borderBottom: "2px solid #f49330",
            userSelect: "none",
          }}
        >
          <Typography sx={{
            fontWeight: 700,
            fontSize: 20,
            background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}>
            Suggest a Character Matchup
          </Typography>
          <Button
            onClick={onClose}
            variant="outlined"
            color="secondary"
            sx={{
              minWidth: 0,
              px: 2,
              py: 0.5,
              borderRadius: 3,
              fontWeight: 700,
              color: "#f49330",
              borderColor: "#f49330",
              "&:hover": {
                color: "#fff176",
                borderColor: "#fff176",
                background: "rgba(244, 147, 48, 0.1)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Cancel
          </Button>
        </Box>
        {/* Content */}
        <Box sx={{ px: 3, py: 3 }}>
          {!session || !profile ? (
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "primary.main" }}>
              Please log in to request a matchup.
            </Typography>
          ) : queueEntry ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Position in Queue:
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#f49330" }}>
                {queuePosition}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              <Autocomplete
                options={filteredOptions1}
                getOptionLabel={opt => opt.name}
                loading={loading1}
                value={char1}
                onChange={(_, val) => setChar1(val)}
                inputValue={search1}
                onInputChange={(_, val) => setSearch1(val)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Character 1"
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        color: "#fff",
                        background: "linear-gradient(135deg, #181818 0%, #232323 100%)",
                        borderRadius: 3,
                        fontSize: 15,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#555",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#f49330",
                      },
                      "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#f49330",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#999",
                      },
                    }}
                  />
                )}
                slotProps={{
                  popper: {
                    sx: { zIndex: 1500 }
                  }
                }}
              />
              <Autocomplete
                options={filteredOptions2}
                getOptionLabel={opt => opt.name}
                loading={loading2}
                value={char2}
                onChange={(_, val) => setChar2(val)}
                inputValue={search2}
                onInputChange={(_, val) => setSearch2(val)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Character 2"
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        color: "#fff",
                        background: "linear-gradient(135deg, #181818 0%, #232323 100%)",
                        borderRadius: 3,
                        fontSize: 15,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#555",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#f49330",
                      },
                      "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#f49330",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#999",
                      },
                    }}
                  />
                )}
                slotProps={{
                  popper: {
                    sx: { zIndex: 1500 }
                  }
                }}
              />
              {error && <Typography color="error">{error}</Typography>}
              {success && <Typography color="success.main">{success}</Typography>}
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={queueEntry && queuePosition && queuePosition > 0}
                  sx={{
                    fontWeight: 700,
                    borderRadius: 3,
                    minWidth: 0,
                    px: 3,
                    py: 1.5,
                    background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                    color: "#181818",
                    fontSize: 14,
                    boxShadow: "0 4px 16px rgba(244, 147, 48, 0.3)",
                    "&:hover": {
                      background: "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
                      boxShadow: "0 6px 20px rgba(244, 147, 48, 0.4)",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Submit
                </Button>
              </Box>
            </Stack>
          )}
        </Box>
      </Paper>
    </Fade>
  );
}